import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, defer, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { noTimeoutContext } from '../http/request-timeout';
import {
  GENERIC_ERROR_MESSAGE,
  GozemApiError,
  SESSION_EXPIRED_MESSAGE,
  toGozemApiError,
} from '@shared/utils/http-error.util';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import { GozemMoneyAccountStore } from '../stores/gozem-money-account.store';
import type {
  TopupMethodsRequest,
  TopupMethodsResponse,
  TopupTokenRequest,
  TopupTokenResponse,
  TopupAuthTxnRequest,
  TopupAuthTxnResponse,
  InitTopupRequest,
  InitTopupResponse,
  TopupFee,
} from '../models';

/**
 * TopupApi — HTTP calls for the "Recharger portefeuille" flow.
 *
 * Four endpoints in order of the dialog steps:
 *  1. POST /api/topup_withdrawal_method  → available topup methods for an amount
 *  2. POST /api/token                    → saved tokens for a processor
 *  3. POST /api/merchant/gozem_money/auth_txn → validate, compute fee + categories
 *  4. POST /api/gozem_money/merchant/init_psp_topup → execute the topup
 *
 * Auth credentials are read from localStorage via Storage.
 *
 * Every method wraps its payload construction in `defer()` so that a synchronous
 * throw from `basePayload()` surfaces as an error *notification* on the returned
 * stream rather than escaping the caller's `switchMap` projection — the latter
 * bypasses the store's inner `catchError`, permanently tears down the pipeline and
 * leaves the dialog stuck on a loading flag with no request sent.
 */
@Injectable({
  providedIn: 'root',
})
export class TopupApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);
  private readonly gmAccountStore = inject(GozemMoneyAccountStore);

  // ── 1. Methods ─────────────────────────────────────────────────────────────

  /**
   * Fetch topup methods available for a given amount.
   * Response methods have relative icon paths — prefix with ConfigService.s3BaseUrl().
   */
  getMethods(amount: number): Observable<TopupMethodsResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: TopupMethodsRequest = { ...base, amount, operation_type: 'Topup' };
      return this.http.post<TopupMethodsResponse>(
        `${environment.apiUrl}/api/topup_withdrawal_method`,
        payload,
      );
    }).pipe(catchError(this.handleError));
  }

  // ── 2. Tokens ──────────────────────────────────────────────────────────────

  /**
   * Fetch saved tokens (payment accounts) for a specific processor.
   * token_service = 1 filters for TOP_UP tokens only.
   */
  getTokens(processorId: string): Observable<TopupTokenResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: TopupTokenRequest = {
        ...base,
        processor_id: processorId,
        token_service: 1,
      };
      return this.http.post<TopupTokenResponse>(`${environment.apiUrl}/api/token`, payload);
    }).pipe(catchError(this.handleError));
  }

  // ── 3. Auth transaction ────────────────────────────────────────────────────

  /**
   * Authorize the transaction: validates the amount, computes fees,
   * and returns available categories + whether PIN is required.
   *
   * @param amount      - Amount to top up
   * @param processorId - ID of the selected processor
   * @param tokenId     - ID of the selected token (_id)
   * @param methodId    - ID of the selected TopupMethod (method.id from topup_withdrawal_method)
   */
  authTxn(
    amount: number,
    processorId: string,
    tokenId: string,
    methodId: string,
  ): Observable<TopupAuthTxnResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: TopupAuthTxnRequest = {
        ...base,
        amount,
        processor_id: processorId,
        token_id: tokenId,
        txn_type: 'Topup',
        transaction_method_id: methodId,
      };
      return this.http.post<TopupAuthTxnResponse>(
        `${environment.apiUrl}/api/merchant/gozem_money/auth_txn`,
        payload,
      );
    }).pipe(catchError(this.handleError));
  }

  // ── 4. Init topup (submit) ─────────────────────────────────────────────────

  /**
   * Execute the topup transaction.
   *
   * @param amount      - Amount to top up
   * @param processorId - ID of the selected processor
   * @param tokenId     - ID of the selected token
   * @param categoryId  - User-selected category ID
   * @param fees        - Fee and commission objects from auth_txn
   * @param message     - Optional user note
   * @param pin         - Optional PIN (only when auth_txn.is_pin_required = true)
   */
  initTopup(
    amount: number,
    processorId: string,
    tokenId: string,
    categoryId: string,
    fees: { fee: TopupFee; commission: TopupFee },
    message: string,
    pin?: string,
  ): Observable<InitTopupResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: InitTopupRequest = {
        ...base,
        amount,
        category: categoryId || undefined,
        processor_id: processorId,
        token_id: tokenId,
        txn_type: 'Topup',
        shared_note: message,
        fee: fees.fee,
        commission: fees.commission,
        payment_tags: [],
        payment_references: [],
        ...(pin ? { pin } : {}),
      };
      // No client-side timeout: this call moves money, so aborting our wait can
      // never be safe — see `noTimeoutContext`.
      return this.http.post<InitTopupResponse>(
        `${environment.apiUrl}/api/gozem_money/merchant/init_psp_topup`,
        payload,
        { context: noTimeoutContext() },
      );
    }).pipe(catchError(this.handleError));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private basePayload(): {
    merchant_id: string;
    account_id: string;
    merchant_user_id: string;
    user_type: 'merchant';
    user_gm_account_id: string;
  } {
    const userData = this.storage.getUserData();
    if (!userData) {
      // Thrown inside each method's `defer()`, so this reaches the caller as a stream
      // error rather than a synchronous throw. GozemApiError (not plain Error) to match
      // SendApi/WithdrawalApi — `remainingAttempts: null` keeps `isAuthStageError()`
      // false, so it is never mistaken for a PIN rejection.
      throw new GozemApiError(SESSION_EXPIRED_MESSAGE, 'NO_USER_DATA', null, null);
    }
    return {
      merchant_id: userData.merchantId,
      // Active account, not accounts[0]: pinning to the first row ignores which
      // merchant the user actually selected. See AccountStore.resolveAccountId.
      account_id: this.accountStore.resolveAccountId(),
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      user_gm_account_id: this.gmAccountStore.primaryAccountId(),
    };
  }

  private handleError(error: unknown): Observable<never> {
    // A non-HttpErrorResponse comes from one of our own pre-request guards (basePayload),
    // already shaped as a GozemApiError with user-facing text. `toGozemApiError` only reads
    // HttpErrorResponse fields and would flatten it to the generic fallback — telling the
    // user to retry a call that can never succeed. Forward it verbatim.
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }
    // toGozemApiError, not `new Error(extractErrorMessage(...))`: the plain Error
    // discarded `error_code` and `remaining_attempts`, so topup could never branch on
    // a business code at all. GozemApiError extends Error, so existing `err.message`
    // readers keep working.
    return throwError(() => toGozemApiError(error, GENERIC_ERROR_MESSAGE));
  }
}
